import type { LessonTab } from '../types';
import { UnitStateKey } from '../../InProgressUnitsPanel/lessonStepState';
import { normalizeIdPart } from '../../InProgressUnitsPanel/loadingStateKeys';

export function lessonsUnitElementId(unitId: string): string {
    return `lessons-unit-${unitId}`;
}

/** Scroll / anchor target for a lesson row inside a unit on the Lessons in-progress list. */
export function lessonsLessonElementId(unitId: string, lessonId: string): string {
    return `lessons-lesson-${normalizeIdPart(unitId)}-${normalizeIdPart(lessonId)}`;
}

type ScheduleScrollIntoViewOptions = {
    delayMs?: number;
    /** Prefer centering (e.g. after completing a lesson). Defaults to nearest / start when tall. */
    block?: ScrollLogicalPosition;
    /** Scroll even when the element is already fully visible. */
    force?: boolean;
};

/**
 * Scroll so an element fits in view when possible; if taller than the scrollport, pin the top.
 * Delay lets expand content paint before measuring.
 */
export function scheduleScrollElementIntoView(
    getElement: () => HTMLElement | null | undefined,
    options?: ScheduleScrollIntoViewOptions
): number {
    const delayMs = options?.delayMs ?? 220;
    const force = options?.force === true;
    const preferredBlock = options?.block;

    return window.setTimeout(() => {
        const el = getElement();
        if (!el) return;

        const rect = el.getBoundingClientRect();
        const scrollParent = el.closest(
            '.overflow-auto, .overflow-y-auto, .overflow-y-scroll'
        ) as HTMLElement | null;
        const viewTop = scrollParent?.getBoundingClientRect().top ?? 0;
        const viewHeight = scrollParent?.clientHeight ?? window.innerHeight;
        const viewBottom = viewTop + viewHeight;

        if (!force && preferredBlock == null && rect.top >= viewTop && rect.bottom <= viewBottom) {
            return;
        }

        const block =
            preferredBlock ?? (rect.height > viewHeight ? 'start' : 'nearest');

        el.scrollIntoView({
            behavior: 'smooth',
            block,
            inline: 'nearest'
        });
    }, delayMs);
}

/** Scroll so a unit card fits in view when possible (or pin the top if it is taller than the viewport). */
export function scheduleScrollUnitCardIntoView(
    unitId: string,
    options?: ScheduleScrollIntoViewOptions
): number {
    const id = (unitId || '').trim();
    if (!id) return 0;

    const targetId = lessonsUnitElementId(id);
    return scheduleScrollElementIntoView(() => document.getElementById(targetId), options);
}

/** Scroll so a lesson card fits in view when possible (or pin the top if it is taller than the viewport). */
export function scheduleScrollLessonCardIntoView(
    unitId: string,
    lessonId: string,
    options?: ScheduleScrollIntoViewOptions
): number {
    const lessonIdPart = (lessonId || '').trim();
    if (!lessonIdPart) return 0;

    const targetId = lessonsLessonElementId(unitId, lessonIdPart);
    return scheduleScrollElementIntoView(() => document.getElementById(targetId), options);
}

/** Scroll / anchor target for an activity row inside a lesson. */
export function lessonsActivityElementId(unitId: string, lessonId: string, activityId: string): string {
    return `lessons-act-${normalizeIdPart(unitId)}-${normalizeIdPart(lessonId)}-${normalizeIdPart(activityId)}`;
}

/** Scroll so an activity row fits in view (e.g. center after finishing). */
export function scheduleScrollActivityCardIntoView(
    unitId: string,
    lessonId: string,
    activityId: string,
    options?: ScheduleScrollIntoViewOptions
): number {
    const activityIdPart = (activityId || '').trim();
    if (!activityIdPart) return 0;

    const targetId = lessonsActivityElementId(unitId, lessonId, activityIdPart);
    return scheduleScrollElementIntoView(() => document.getElementById(targetId), options);
}

/**
 * Decide which Lessons sub-tab lists this unit today (aligned with getUnitsForTab filtering).
 */
export function lessonTabForUnitBackendState(
    state: string | undefined
): Exclude<LessonTab, 'create' | 'addTopics' | 'assess'> {
    const s = (state ?? '').trim();
    if (s === UnitStateKey.Reviewing) return 'review';
    if (s === UnitStateKey.Complete) return 'done';
    return 'inprogress';
}
